import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, NEVER, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { AppConfigService } from '../services/app-config.service';

// The Lab's own sign-in and password-recovery endpoints. With SSO switched on
// the backend answers each of them with a bare 404, as if the route did not
// exist.
const LOCAL_AUTH_PATHS = [
  '/users/login-challenge',
  '/users/login',
  '/users/password-reset',
  '/users/password-change',
];

/**
 * Rescues a page that was loaded while SSO was off and is still showing the
 * Lab's own sign-in or recovery form after SSO was switched on server-side.
 *
 * The config read at boot says SSO is off, so the form calls a local
 * endpoint, which now 404s. On that 404 the config is re-read; if SSO is on
 * now, the browser goes to sso-fe exactly as a fresh page load would. Any
 * other outcome hands the original error back to the page untouched.
 */
@Injectable()
export class SsoSwitchInterceptor implements HttpInterceptor {
  constructor(private appConfig: AppConfigService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!isLocalAuthCall(request.url)) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error) => {
        if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
          return throwError(() => error);
        }
        return from(this.appConfig.refresh()).pipe(
          switchMap((config) => {
            if (!config.ssoEnabled || !config.ssoFrontendUrl) {
              return throwError(() => error);
            }
            const rd = encodeURIComponent(window.location.host);
            this.redirect(`${config.ssoFrontendUrl}/login?rd=${rd}`);
            // The page is navigating away; never settle, so the form does not
            // flash an error in the moment before it unloads.
            return NEVER;
          }),
        );
      }),
    );
  }

  private redirect(url: string): void {
    window.location.replace(url);
  }
}

function isLocalAuthCall(url: string): boolean {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
  return LOCAL_AUTH_PATHS.some((p) => path.endsWith(p));
}
